import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import SummaryHeader from "../Header/SummaryHeader";
import ProjectSummaryDefitions from "../Defintions/ProjectSummaryDefitions";
import { setProjectDetails } from "../userSlice";
import { setSession, getSession } from "../utils/session";

const ProjectSummary = () => {
  const email = useSelector((state) => state.user.email);
  const nameBeforeAt = email?.split("@")[0] || "";

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [selectedOrg, setSelectedOrg] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [selectedProject, setSelectedProject] = useState([]);

  const [org, setOrg] = useState([]);
  const [status, setStatus] = useState([]);
  const [project, setProject] = useState([]);
  const [projectData, setProjectData] = useState([]);
  const [activeProjectId, setActiveProjectId] = useState(null);
  const [loading, setLoading] = useState(false);

  // RESTORE SESSION
  useEffect(() => {
    if (!email) {
      const session = getSession();
      if (session?.email) {
        dispatch(
          setProjectDetails({
            email: session.email,
            projectName: session.projectName,
            projectId: session.projectId,
            organizationName: session.organizationName,
            roleId: session.roleId || null,
          })
        );
      } else navigate("/");
    }
  }, [email, dispatch, navigate]);

  const getQueryParams = () => {
    const params = new URLSearchParams();
    selectedProject.forEach((p) => params.append("project_name", p));
    selectedOrg.forEach((o) => params.append("organization_name", o));
    selectedStatus.forEach((st) => params.append("project_status", st));

    const session = getSession();
    const userEmail = email || session?.email;
    if (userEmail) params.append("userEmail", userEmail);

    return params.toString();
  };

  // FILTER APIs
  useEffect(() => {
    fetch(`https://api-project360-insights.haqdarshak.com/api/summary/unique-org/?${getQueryParams()}`)
      .then((res) => res.json())
      .then((data) => setOrg(data.organization_name || []))
      .catch(console.error);
  }, [selectedProject, selectedStatus]);

  useEffect(() => {
    fetch(`https://api-project360-insights.haqdarshak.com/api/summary/unique-status/?${getQueryParams()}`)
      .then((res) => res.json())
      .then((data) => setStatus(data.project_status || []))
      .catch(console.error);
  }, [selectedOrg, selectedProject]);

  useEffect(() => {
    fetch(`https://api-project360-insights.haqdarshak.com/api/summary/unique-projects/?${getQueryParams()}`)
      .then((res) => res.json())
      .then((data) => setProject(data.project_name || []))
      .catch(console.error);
  }, [selectedOrg, selectedStatus]);

  // FETCH ALL PAGES — parallel fetching for speed
  useEffect(() => {
    const fetchAllPages = async () => {
      setLoading(true);
      try {
        const queryParams = getQueryParams();

        // Step 1: Fetch page 1 to get total count
        const firstRes = await fetch(
          `https://api-project360-insights.haqdarshak.com/api/project-summary/?${queryParams}&page=1&page_size=50`
        );
        const firstData = await firstRes.json();
        const allResults = [...(firstData.results || [])];
        const totalPages = Math.ceil((firstData.count || 0) / 50);

        // Step 2: Fetch all remaining pages IN PARALLEL
        if (totalPages > 1) {
          const pageNumbers = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
          const responses = await Promise.all(
            pageNumbers.map((page) =>
              fetch(
                `https://api-project360-insights.haqdarshak.com/api/project-summary/?${queryParams}&page=${page}&page_size=50`
              ).then((res) => res.json())
            )
          );
          responses.forEach((data) => {
            if (data.results) allResults.push(...data.results);
          });
        }

        // Remove duplicate projects by name
        const uniqueProjects = Array.from(
          new Map(allResults.map((item) => [item.project_name, item])).values()
        );

        setProjectData(uniqueProjects);

        // Store role from first project
        if (uniqueProjects.length) {
          const roleId = uniqueProjects[0].role_id;
          if (roleId) {
            dispatch(setProjectDetails({ email, roleId }));
            const session = getSession() || {};
            setSession({ ...session, roleId });
          }
        }
      } catch (err) {
        console.error("Error fetching projects:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAllPages();
  }, [selectedOrg, selectedStatus, selectedProject, email, dispatch]);

  const MultiSelectDropdown = ({ options, selected, setSelected, placeholder }) => (
    <Select
      isMulti
      options={options.map((opt) => ({ label: opt, value: opt }))}
      value={selected.map((val) => ({ label: val, value: val }))}
      onChange={(opts) => setSelected(opts ? opts.map((o) => o.value) : [])}
      placeholder={placeholder}
      closeMenuOnSelect={false}
    />
  );

  const handleCardClick = (projectId) => {
    setActiveProjectId(activeProjectId === projectId ? null : projectId);
  };

  const handleViewDetails = (row) => {
    dispatch(
      setProjectDetails({
        email,
        projectName: row.project_name,
        projectId: row.PID,
        organizationName: row.organization_name,
        roleId: row.role_id || null,
      })
    );
    const session = getSession() || {};
    setSession({
      ...session,
      email,
      projectName: row.project_name,
      projectId: row.PID,
      organizationName: row.organization_name,
      roleId: row.role_id || null,
    });
    navigate("/Project-Level");
  };

  const clearValue = () => {
    setSelectedOrg([]);
    setSelectedProject([]);
    setSelectedStatus([]);
  };

  const handleExport = () => {
    if (!projectData.length) return alert("No data");
    const headers = Object.keys(projectData[0]);
    const csv = [
      headers.join(","),
      ...projectData.map((row) =>
        headers.map((h) => JSON.stringify(row[h] ?? "")).join(",")
      ),
    ].join("\n");
    const blob = new Blob([csv]);
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "Project_Summary.csv";
    link.click();
  };

  const displayedProjects = activeProjectId
    ? projectData.filter((p) => p.PID === activeProjectId)
    : projectData;

  // ✅ Show full-screen Access Denied — no header/filters shown
  if (!loading && projectData.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="bg-red-100 rounded-full p-6 mb-4">
          <svg
            className="w-16 h-16 text-red-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0zM12 17h.01"
            />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-red-600 mb-2">Access Denied</h2>
        <p className="text-gray-500 text-center max-w-sm">
          You don't have access to any projects. Please contact your administrator.
        </p>
      </div>
    );
  }

  return (
    <div>
      <SummaryHeader />

      <div className="mt-20 px-6 flex justify-between">
        <h2 className="text-xl font-bold">Hello {nameBeforeAt}!</h2>
        <div className="flex gap-4">
          <button onClick={handleExport} className="bg-green-600 text-white px-4 py-2 rounded">
            Export
          </button>
          <ProjectSummaryDefitions />
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-4 gap-2 px-6 mt-4">
        <MultiSelectDropdown
          options={org}
          selected={selectedOrg}
          setSelected={setSelectedOrg}
          placeholder="Org"
        />
        <MultiSelectDropdown
          options={project}
          selected={selectedProject}
          setSelected={setSelectedProject}
          placeholder="Project"
        />
        <MultiSelectDropdown
          options={status}
          selected={selectedStatus}
          setSelected={setSelectedStatus}
          placeholder="Status"
        />
        <button onClick={clearValue} className="bg-red-600 text-white rounded">
          Clear
        </button>
      </div>

      {/* Loading Spinner */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-4 text-lg font-medium text-gray-700">Loading Projects...</p>
          </div>
        </div>
      ) : (
        /* Project Cards */
        <div className="mt-6 px-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayedProjects.map((proj) => (
            <div
              key={proj.PID}
              onClick={() => handleCardClick(proj.PID)}
              className={`cursor-pointer p-4 rounded shadow-lg transition transform hover:-translate-y-1 hover:shadow-2xl ${
                activeProjectId === proj.PID
                  ? "border-2 border-indigo-600 bg-indigo-50"
                  : "bg-white"
              }`}
            >
              <h3 className="text-lg font-bold mb-2">{proj.project_name}</h3>
              <p>
                <span className="font-semibold">PID:</span> {proj.PID}
              </p>
              <p>
                <span className="font-semibold">Organization:</span> {proj.organization_name}
              </p>
              <p>
                <span className="font-semibold">Status:</span> {proj.project_status}
              </p>
              <p>
                <span className="font-semibold">Start:</span> {proj.project_start_date}
              </p>
              <p>
                <span className="font-semibold">End:</span> {proj.project_end_date}
              </p>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewDetails(proj);
                }}
                className="mt-4 bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
              >
                View Project Level Data
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProjectSummary;